import React, { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Box, Typography, Button } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

/**
 * ProtectedRoute component for route-level authentication and authorization
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string[]} props.requiredRoles - Array of roles required to access the route
 * @param {boolean} props.requireAuth - Whether authentication is required (default: true)
 * @param {string} props.redirectTo - Path to redirect to if not authenticated
 */
export function ProtectedRoute({ 
  children, 
  requiredRoles = [], 
  requireAuth = true,
  redirectTo = '/'
}) {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  
  const userRoles = useMemo(
    () => instance.getActiveAccount()?.idTokenClaims?.roles || [],
    [instance]
  );

  // Check if user is authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user has required roles
  if (requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));
    
    if (!hasRequiredRole) {
      return <AccessDenied requiredRoles={requiredRoles} userRoles={userRoles} />;
    }
  }

  return children;
}

/**
 * Access Denied component shown when user doesn't have required permissions
 */
function AccessDenied({ requiredRoles, userRoles }) {
  const { instance } = useMsal();
  
  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="60vh"
      p={3}
    >
      <LockIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
      <Typography variant="h4" gutterBottom color="error">
        Access Denied
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" mb={3}>
        You do not have permission to view this page.
      </Typography>
      
      {requiredRoles.length > 0 && (
        <Box mb={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Required role(s): <strong>{requiredRoles.join(', ')}</strong>
          </Typography>
          {userRoles.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Your role(s): <strong>{userRoles.join(', ')}</strong>
            </Typography>
          )}
        </Box>
      )}
      
      <Box display="flex" gap={2}>
        <Button variant="outlined" onClick={handleGoBack}>
          Go Back
        </Button>
        <Button variant="contained" onClick={handleGoHome}>
          Go to Home
        </Button>
      </Box>
    </Box>
  );
}

export default ProtectedRoute;

